import { z } from "zod";

// An optional field the partner left blank stores NULL, not the "" an untouched
// input hands back.

interface RequiredTextRules {
  missing: string;
  maxLength: number;
}

export function requiredText({ missing, maxLength }: RequiredTextRules) {
  return z.string().trim().min(1, missing).max(maxLength, `Max ${maxLength} znaků`);
}

/** A required field the form still hands over as null for an unsaved value, so
 *  the shape is `requiredCoordinate`'s: `blankToNull` first, refuse the null last.
 *  A shape rule chained onto this must let null through — the presence refusal
 *  already covers it, and "Zadejte …" must beat a format complaint about a field
 *  the partner never typed into. */
export function requiredNullableText({ missing, maxLength }: RequiredTextRules) {
  return z
    .preprocess(
      blankToNull,
      z
        .string({ required_error: missing, invalid_type_error: missing })
        .trim()
        .max(maxLength, `Max ${maxLength} znaků`)
        .nullable(),
    )
    .refine((value): boolean => value !== null, { message: missing });
}

export function optionalText({ maxLength }: { maxLength: number }) {
  return z
    .string()
    .trim()
    .max(maxLength, `Max ${maxLength} znaků`)
    .nullable()
    .transform((value) => (value === null || value === "" ? null : value));
}

/** An emptied input arrives as "" from a text control and as NaN from a number
 *  one; both mean the partner left the field blank. An absent key is not blank —
 *  it never reached a control at all — so `undefined` passes through and the
 *  schema refuses it instead of reading it as a deliberate clearing. */
export function blankToNull(value: unknown): unknown {
  if (typeof value === "string" && value.trim() === "") return null;
  if (typeof value === "number" && Number.isNaN(value)) return null;
  return value;
}

interface CoordinateRules {
  limit: number;
  outOfRange: string;
  missing: string;
}

/** A business without coordinates cannot be placed on the customer app's map.
 *  `blankToNull` first, so an emptied field is refused as missing rather than as
 *  out of range. `: boolean` second, or TypeScript infers a type predicate and zod
 *  drops the null that `businessProfileValues` still hands the form. */
export function requiredCoordinate({ limit, outOfRange, missing }: CoordinateRules) {
  return z
    .preprocess(
      blankToNull,
      z
        .number({ invalid_type_error: outOfRange, required_error: missing })
        .min(-limit, outOfRange)
        .max(limit, outOfRange)
        .nullable(),
    )
    .refine((coordinate): boolean => coordinate !== null, { message: missing });
}
